import { Award, Ban, CheckCircle2, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { AwardCategoryModal } from '../components/AwardCategoryModal';
import { awardCategoriesApi } from '../praiseApi';
import type { AwardCategoryDetail } from '../praiseTypes';

type TabKey = 'info' | 'checklist';

type Pending = 'details' | 'checklist' | 'delete' | 'restore' | null;

/**
 * One award category, on the header layout, with TWO tabs — the attrition category's shape. Info
 * carries Details (its own Edit) and the Audit; the document checklist is its OWN tab with its own
 * Edit and its own write (`PUT …/documents`), and neither form can reach the other's fields.
 */
export function AwardCategoryProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [category, setCategory] = useState<AwardCategoryDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      awardCategoriesApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setCategory(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this category.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'PRAISE',
    { label: 'Settings', to: '/praise/settings' },
    { label: 'Award Categories', to: '/praise/settings/award-categories' },
    category?.name ?? 'Category',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={2} />
      </div>
    );
  }

  const record = category;

  async function runConfirmed() {
    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await awardCategoriesApi.deactivate(record.id);
      else await awardCategoriesApi.activate(record.id);

      toast.success(`${record.name} was ${pending === 'delete' ? 'deleted' : 'restored'}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="award-category-profile"
        header={
          <ProfileHeader
            id="award-category-profile-header"
            icon={<Award className="h-6 w-6" aria-hidden="true" />}
            title={record.name}
            meta={[
              `${record.documents.length} ${record.documents.length === 1 ? 'document' : 'documents'}`,
              `${record.awardCount} ${record.awardCount === 1 ? 'award' : 'awards'}`,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: each section's card owns its own.
            settings={{
              onHistory: () => setHistoryOpen(true),
              menuItems: record.isActive
                ? []
                : [{ key: 'restore', label: 'Restore', permission: 'award-categories.delete', onClick: () => setPending('restore') }],
              onDelete: () => setPending('delete'),
              deletePermission: 'award-categories.delete',
              deleteDisabledReason: record.isActive ? null : 'This category is already deleted.',
            }}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="award-category-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This page may be out of date: ${error.message}`}
            />
          ) : !record.isActive ? (
            <ProfileBanner
              id="award-category-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This category is deleted. It is not offered when an award is opened; the awards already of it keep it."
            />
          ) : record.documents.length === 0 ? (
            <ProfileBanner
              id="award-category-profile-banner"
              icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
              message="This category has no documents on its checklist, so no award can be opened under it yet."
            />
          ) : (
            <ProfileBanner
              id="award-category-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="Every nominee for an award of this category is asked for each document on its checklist."
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'checklist',
            label: (
              <>
                Checklist <CountBadge count={record.documents.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'checklist' ? (
          <ProfileCard
            id="award-category-profile-checklist"
            title="Document requirements"
            count={record.documents.length}
            onEdit={() => setPending('checklist')}
            editPermission="award-categories.update"
            empty={
              record.documents.length === 0
                ? {
                    title: 'No documents on the checklist yet',
                    description: 'An award can be opened under this category once it asks for at least one document.',
                  }
                : undefined
            }
          >
            {record.documents.length === 0 ? null : (
              <PreviewTable
                variant="table-display"
                rows={record.documents.map((document) => ({
                  label: document.name,
                  value: (
                    <span className="flex min-w-0 flex-col gap-0.5">
                      {/* The DEFAULT a new award starts this document's criterion at — an award keeps its own. */}
                      {/* An absent value is an em dash — the table-display rule. */}
                      <span className={document.defaultMaxPoints != null ? 'font-mono' : 'text-slate-400'}>
                        {document.defaultMaxPoints != null ? `${document.defaultMaxPoints} points by default` : '—'}
                      </span>
                      {document.description ? <span className="text-xs text-slate-400">{document.description}</span> : null}
                    </span>
                  ),
                }))}
              />
            )}
          </ProfileCard>
        ) : (
          <>
            <ProfileCard
              id="award-category-profile-details"
              title="Details"
              onEdit={() => setPending('details')}
              editPermission="award-categories.update"
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Name', value: record.name },
                  { label: 'Description', value: record.description },
                  { label: 'Order', value: String(record.sortOrder), mono: true },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="award-category-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending === 'details' || pending === 'checklist' ? (
        <AwardCategoryModal
          open
          section={pending}
          editing={record}
          onClose={() => setPending(null)}
          onSaved={(saved) => {
            setCategory(saved);
            setPending(null);
          }}
        />
      ) : null}

      {pending === 'delete' || pending === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending === 'delete' ? 'Delete category' : 'Restore category'}
          danger={pending === 'delete'}
          requireConfirm={pending === 'delete'}
          requireCredential={pending === 'delete'}
          loading={running}
          id={`award-category-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'delete'
                  ? `"${record.name}" stops being offered when an award is opened. The ${record.awardCount} already of it keep it.`
                  : `"${record.name}" is offered again when an award is opened.`}
              </p>
              <p>Nothing is erased — a deleted category can be restored.</p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal open={historyOpen} onClose={() => setHistoryOpen(false)} module="award-categories" entityId={record.id} />
    </div>
  );
}
